/**
 * Tiered quote on the cost sheet (JF 2026-10-12): shown when the estimate was started with the tiered-quote switch.
 * The AI priced every quantity itself — per line a unit price, yield and reason, plus CM and processes; this section
 * shows the quantities side by side, the AI's reasons per line, and lets the AM correct any number of any quantity.
 * The program only does the sums (price × yield, quote coefficients, one-off costs over the quantity).
 */
import { useState } from "react";
import { FULL_FEATURES } from "@ic/engine";
import { actPrice, addRows, cmLines, coefOf, isSet, n, switchTier, tiersOf, totals, type LineKind } from "@ic/engine";
import { setMoney, useMoney } from "../../shared/money.ts";
import { useStore } from "../../store.ts";
import { COEF_FIELDS } from "./coef.ts";
import { ProfitOnly } from "../../shared/ui/ProfitVisibility.tsx";
import { useT } from "../../shared/i18n.ts";
import { Field } from "../../shared/ui/common.tsx";

export function TierQuote() {
  const t = useT();
  const s = useStore((st) => st.sheet);
  const profitVisible = useStore((st) => FULL_FEATURES && st.sheet.quote);
  const { editSheet } = useStore.getState();
  const [detail, setDetail] = useState<number | null>(null);
  const m = useMoney();
  if (!s.tiers?.qtys.length) return null;

  const tiers = tiersOf(s);
  const tt = tiers.map((x) => totals(x.sheet));
  const co = coefOf(s);
  const rows: [string, (x: (typeof tt)[number]) => number, string?][] = [
    [t("Fabric", "面料"), (x) => x.fab, "fab"], [t("Trim", "辅料"), (x) => x.trim, "trim"], ["CM", (x) => x.cm, "cm"],
    [t("Additional incl. one-off per pc", "外部费用（含一次性费用分摊）"), (x) => x.add, "add"],
    ["Total FOB · actual", (x) => x.fob], ["Total FOB · quoted", (x) => x.qfob], ["Grand Total FOB", (x) => x.grand], ["GP / pc", (x) => x.gp],
  ];
  const active = String(s.tiers.active ?? "");
  // the quantity shown above is the cost sheet itself: its numbers are edited there directly
  const setLine = (k: LineKind, i: number, q: string, f: "px" | "yld", v: string) => editSheet((x) => {
    const r = x[k][i];
    if (!x.fx && m.cur === "CNY") x.fx = m.fx;
    if (q === active) { if (f === "px") setMoney(r, m.typed(v)); else r.yld = v; return; }
    const cur = { ...(r.tq?.[q] || { px: r.px, yld: r.yld, why: "", ...(isSet(r.cny) ? { cny: r.cny } : {}) }) };
    if (f === "px") setMoney(cur, m.typed(v)); else cur.yld = v;
    r.tq = { ...(r.tq || {}), [q]: { ...cur, why: t("edited by you", "你手动修改") } };
  });
  const d = detail != null ? tiers.find((x) => x.qty === detail) : null;
  const dq = d ? String(d.qty) : "";
  const missing = tiers.filter((x) => x.missing > 0);
  // Additional amounts entered for the whole order — spread over each quantity (JF 2026-10-13)
  const orderSum = addRows(s).filter((r) => r.per === "order").reduce((a, r) => a + n(r.amount), 0);

  return (
    <div className="sec tint-tier" id="tierSec">
      <div className="sec-h"><b>{t("Tiered quote", "阶梯报价")}</b>
        <span className="dim">{t("the AI priced each quantity (unit price, yield, CM); the cost sheet above shows one of them — \"detail\" for the lines and reasons, any number can be corrected",
          "AI 按每个数量分别定价（单价、用量、CM）；上面的成本表显示其中一档——「明细」看每行价格和理由，数字都可以改")}</span>
        <span className="lnk" id="tierOff" style={{ marginLeft: "auto" }} onClick={() => {
          if (!confirm(t("Remove the tiered quote from this sheet?", "从这张成本表去掉阶梯报价？"))) return;
          editSheet((x) => { x.tiers = undefined; for (const k of ["fab", "trim"] as const) for (const r of x[k]) delete r.tq; });
        }}>{t("Remove", "去掉阶梯报价")}</span></div>
      {missing.length > 0 && <div className="warn" style={{ margin: "10px 14px 0" }} id="tierMissing">{t(
        "Some lines have no AI price for some quantities (added later, or the AI left them out) — they use this sheet's numbers. Re-price with the AI or type them in the detail.",
        "部分行在某些数量档没有 AI 价格（后来手动加的，或 AI 漏了）——先用成本表本身的数字。可以重新让 AI 定价，或在明细里手填。")}</div>}
      <div className="gs"><table className="g" id="tierTbl" style={{ minWidth: 640 }}><tbody>
        <tr><th>{t("Per piece", "每件")}</th>{tiers.map((x) => (
          <th className={"num" + (String(x.qty) === active ? " tieron" : "")} key={x.qty}>
            {String(x.qty) === active ? <b>{x.qty} {t("pcs", "件")} · {t("shown above", "上面显示的")}</b>
              : <span className="lnk" data-tier-show={x.qty} onClick={() => editSheet((y) => switchTier(y, x.qty))} title={t("Show this quantity in the cost sheet above", "上面的成本表换成这个数量")}>{x.qty} {t("pcs", "件")} · {t("show", "显示")}</span>}
            {" · "}<span className={"lnk" + (detail === x.qty ? " on" : "")} data-tier-detail={x.qty} onClick={() => setDetail(detail === x.qty ? null : x.qty)}>
            {detail === x.qty ? t("hide", "收起") : t("detail", "明细")}</span></th>))}</tr>
        {rows.filter(([label, , tint]) => FULL_FEATURES || tint || label === "Total FOB · actual").map(([label, get, tint]) => (
          <tr key={label} className={tint ? "tint-" + tint : "tierfob"}><td style={{ whiteSpace: "nowrap" }}>{label}</td>
            {/* the CM of a quantity is edited row by row on the cost sheet above while that quantity is shown (JF 2026-10-13) */}
            {tt.map((x, i) => <td key={i} className="num" data-tier-v={(tint || label) + ":" + tiers[i].qty}>{tint || label === "Total FOB · actual" ? m.fmt(get(x)) : <ProfitOnly>{m.fmt(get(x))}</ProfitOnly>}</td>)}</tr>
        ))}
        {/* quote coefficients per quantity (JF 2026-10-13): set them in the coefficient bar while that quantity is shown */}
        {profitVisible && <tr data-tiercoef="row"><td style={{ whiteSpace: "nowrap" }}>{t("Quote coefficients", "报价系数")}</td>{tiers.map((x, i) => {
          const c0 = coefOf(tiers[0].sheet), c = coefOf(x.sheet);
          const diff = COEF_FIELDS.filter(([k]) => c[k] !== c0[k]).map(([k, en, zh, kind]) => `${t(en, zh)} ${kind === "pct" ? +((c[k] ?? 0) * 100).toFixed(2) + "%" : c[k]}`);
          return <td key={i} className="num dim" style={{ fontSize: 12 }}>{i === 0 ? t("base", "基准") : diff.length ? diff.join(" · ") : t("same", "相同")}</td>;
        })}</tr>}
        {FULL_FEATURES && <tr className="tierfob"><td>GP %</td>{tt.map((x, i) => <td key={i} className="num"><ProfitOnly>{(x.gpp * 100).toFixed(1)}%</ProfitOnly></td>)}</tr>}
      </tbody></table></div>
      {d && (
        <div className="tierdet" id="tierDetail">
          <b>{d.qty} {t("pcs — the AI's price per line", "件——AI 给的每行价格")}</b>
          {s.tiers.note?.[dq] && <div className="dim" style={{ marginBottom: 6 }}>{s.tiers.note[dq]}</div>}
          <div className="gs"><table className="g" style={{ minWidth: 700 }}><tbody>
            <tr><th>{t("Line", "项目")}</th><th className="num">{t("Actual unit price", "成本单价")} {m.sym}</th><th className="num">{t("Actual yield", "成本用量")}</th><th className="num">{t("Actual amount", "成本金额")} {m.sym}/{t("pc", "件")}</th><th>{t("AI's reason", "AI 理由")}</th></tr>
            {(["fab", "trim"] as const).flatMap((k) => s[k].map((o, i) => {
              const x = o.tq?.[dq];
              const r = d.sheet[k][i];
              return (
                <tr key={k + i} className={"tint-" + k} data-tier-line={k + i}><td>{o.name || (k === "fab" ? t("Fabric", "面料") : t("Trim", "辅料"))}
                  {o.px !== r.px && <span className="dim"> ({t("sheet", "成本表")} {m.shown(o.px, o.cny)})</span>}</td>
                  <td><Field className="n" value={m.shown(r.px, r.cny)} onChange={(v) => setLine(k, i, dq, "px", v)} /></td>
                  <td><Field className="n" value={r.yld} onChange={(v) => setLine(k, i, dq, "yld", v)} /></td>
                  <td className="num">{m.num(actPrice(r, co))}</td>
                  <td className="dim">{x && !x.none ? x.why || "—" : t("no AI price for this quantity — the sheet's numbers", "AI 没给这一档——用成本表的数字")}</td></tr>
              );
            }))}
            {cmLines(d.sheet).filter((l) => isSet(l.px)).map((l, j) => (
              <tr className="tint-cm" key={"cm" + l.key} data-tier-cmline={l.key}><td>CM · {l.part ? t(l.en, l.zh) : l.name}</td><td className="num">{m.shown(l.px, l.cny)}{l.per === "order" ? " " + t("/ order", "/ 整单") : ""}</td><td colSpan={1}></td>
                <td className="num">{m.num(l.actual)}</td><td className="dim">{j === 0 ? s.tiers!.cm[dq]?.why || "—" : ""}</td></tr>
            ))}
            {orderSum > 0 && <tr className="tint-add"><td>{t("Whole-order amounts", "整单金额")}</td><td colSpan={3}></td>
              <td className="dim">{t(`${m.fmt(orderSum)} ÷ ${d.qty} pcs = ${m.fmt(orderSum / d.qty)} / pc`,
                `${m.fmt(orderSum)} ÷ ${d.qty} 件 = ${m.fmt(orderSum / d.qty)} / 件`)}</td></tr>}
          </tbody></table></div>
        </div>
      )}
    </div>
  );
}
